import React from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider,
  Grid,
} from "@mui/material";
import { Code, CalendarMonth } from "@mui/icons-material";

export const ApiPage: React.FC = () => {
  const endpoints = [
    {
      method: "GET",
      path: "/data/{year}.json",
      description: "Get merged holidays for a specific year (array format)",
      example: "/data/2026.json",
      returns: "MergedHoliday[]",
    },
    {
      method: "GET",
      path: "/data/holidays.json",
      description: "Get all years of merged holidays in a single file",
      example: "/data/holidays.json",
      returns: "AllMergedOutput",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <Code sx={{ fontSize: 40, color: "primary.main" }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: "text.primary" }}>
              API Documentation
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            Access Australian public holiday data programmatically
          </Typography>
        </Box>

        {/* Overview */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Overview
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Our API provides access to Australian public holiday data in JSON format. No
            authentication required - simply fetch the data files directly.
          </Typography>
        </Paper>

        {/* Available Endpoints */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Available Endpoints
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Returns</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {endpoints.map((endpoint, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip label={endpoint.method} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <code style={{ fontSize: "0.875rem" }}>{endpoint.path}</code>
                    </TableCell>
                    <TableCell>{endpoint.description}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: "0.75rem" }}>{endpoint.returns}</code>
                    </TableCell>
                    <TableCell>
                      <a
                        href={endpoint.example}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <code
                          style={{ fontSize: "0.875rem", color: "#2563eb", cursor: "pointer" }}
                        >
                          {endpoint.example}
                        </code>
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Data Structure - Type Definitions */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Type Definitions
          </Typography>
          <Box
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}
          >
            {/* Left Column */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                State
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.875rem",
                  mb: 3,
                }}
              >
                {`type State = "WA" | "NT" | "SA" | "QLD" | 
           "NSW" | "ACT" | "VIC" | "TAS"`}
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                MergedHoliday
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.75rem",
                }}
              >
                {`interface MergedHoliday {
  id: string
  dateString: string
  holidayName: string
  information?: string
  infoLink?: string
  type: "state" | "federal" | "other"
  states: State[]
  sources?: PublicHoliday[]
  partDay: boolean
  partDayInfo?: string
}`}
              </Box>
            </Box>

            {/* Right Column */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                PublicHoliday
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.75rem",
                  mb: 3,
                }}
              >
                {`interface PublicHoliday {
  id: string
  dateString: string
  holidayName: string
  information?: string
  infoLink?: string
  type: "state" | "federal" | "other"
  state: State
  partDay: boolean
  partDayInfo?: string
}`}
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                AllMergedOutput
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.75rem",
                }}
              >
                {`interface AllMergedOutput {
  [year: number]: MergedHoliday[]
}`}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Year File Example */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Year File Example (e.g., 2026.json)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Returns an array of merged holidays for that year. Holidays are de-duplicated
            across states - if the same holiday occurs in multiple states, it appears once with
            all applicable states listed.
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              fontSize: "0.875rem",
              mb: 3,
            }}
          >
            {`[
  {
    "id": "ph_1",
    "dateString": "20260101",
    "holidayName": "New Year's Day",
    "information": "The first day of the year",
    "infoLink": "https://example.com/new-years-day",
    "type": "federal",
    "states": ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"],
    "sources": [
      {
        "id": "nsw_ph_1",
        "dateString": "20260101",
        "holidayName": "New Year's Day",
        "type": "federal",
        "state": "NSW",
        "partDay": false
      },
      {
        "id": "vic_ph_1",
        "dateString": "20260101",
        "holidayName": "New Year's Day",
        "type": "federal",
        "state": "VIC",
        "partDay": false
      }
    ],
    "partDay": false
  },
  {
    "id": "ph_2",
    "dateString": "20260126",
    "holidayName": "Australia Day",
    "type": "federal",
    "states": ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"],
    "sources": [
      {
        "id": "nsw_ph_2",
        "dateString": "20260126",
        "holidayName": "Australia Day",
        "type": "federal",
        "state": "NSW",
        "partDay": false
      }
    ],
    "partDay": false
  }
]`}
          </Box>

          {/* All Years File Example */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            All Years File Example (holidays.json)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Returns an object with years as keys and arrays of merged holidays as values. Use
            this if you want all available years in a single request.
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              fontSize: "0.875rem",
            }}
          >
            {`{
  "2026": [
    {
      "id": "ph_1",
      "dateString": "20260101",
      "holidayName": "New Year's Day",
      "type": "federal",
      "states": ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"],
      "sources": [
        {
          "id": "nsw_ph_1",
          "dateString": "20260101",
          "holidayName": "New Year's Day",
          "type": "federal",
          "state": "NSW",
          "partDay": false
        }
      ],
      "partDay": false
    }
  ],
  "2027": [
    {
      "id": "ph_100",
      "dateString": "20270101",
      "holidayName": "New Year's Day",
      "type": "federal",
      "states": ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"],
      "sources": [
        {
          "id": "nsw_ph_100",
          "dateString": "20270101",
          "holidayName": "New Year's Day",
          "type": "federal",
          "state": "NSW",
          "partDay": false
        }
      ],
      "partDay": false
    }
  ]
}`}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
